import React from "react";
import { Helmet } from "react-helmet-async";
import { siteMeta } from "./config";

type SEOProps = {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  locale?: string;
};

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  path = "/",
  image,
  locale = "da",
}) => {
  const fullTitle = title
    ? `${title} | ${siteMeta.siteName}`
    : siteMeta.defaultTitle;
  const url = `${siteMeta.baseUrl}${path}`;
  const img = image?.startsWith("http")
    ? image
    : `${siteMeta.baseUrl}${image || siteMeta.defaultImage}`;
  const desc = description || siteMeta.defaultDescription;

  const ldOrg = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteMeta.siteName,
    url: siteMeta.baseUrl,
    logo: `${siteMeta.baseUrl}${siteMeta.defaultImage}`,
  };

  return (
    <Helmet htmlAttributes={{ lang: locale }}>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
      {siteMeta.twitterHandle && (
        <meta name="twitter:site" content={siteMeta.twitterHandle} />
      )}

      {/* Hreflang */}
      {siteMeta.locales.map((l) => (
        <link
          key={l}
          rel="alternate"
          hrefLang={l}
          href={`${siteMeta.baseUrl}/${path.replace(/^\//, "")}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(ldOrg)}</script>
    </Helmet>
  );
};
